import { useEffect, useState } from 'react';

import { TextField } from '../../../../../components/inputs/TextField';
import { EmptyState } from '../../../../../components/EmptyState';
import { identificationApi } from '../../../identification/identificationApi';
import type { IdentificationType } from '../../../identification/identificationTypes';
import { personnelApi } from '../../personnelApi';
import type { PersonnelDetail } from '../../personnelTypes';
import { NO_CATALOGS, samplePersonnel } from '../../utils/samplePersonnel';
import { PersonnelSectionModal } from './PersonnelSectionModal';

/** The identification numbers need no catalogs — only the type list. */
import { useSectionForm } from './sectionForm';

/**
 * The Identification card's editor — one field per type in the catalog.
 *
 * **Every active type gets a field, not one field per number already held.** The card lists
 * what is recorded; the form has to offer what could be, or a number the record does not yet
 * carry has nowhere to be typed.
 *
 * **IT REVEALS ON OPEN.** The numbers moved to `rsys_pii` on 17 Sep 2026, so the record no
 * longer carries them and this form fetches each one — an audited reveal per card, the way the
 * Personal card reveals a name when its panel opens. Product call: it keeps one modal for the
 * whole section, and nothing is lost on save.
 *
 * **A field it could not reveal is sent as `null`, which means "leave it alone".** That is the
 * case for a reader holding `personnel.update` but not `personnel.reveal`: they can correct
 * the place and the dates, they never see a number, and saving cannot wipe one. A field that
 * WAS revealed and then cleared sends an empty string, which still removes the number — so the
 * one thing that changed is that a number nobody could see is no longer destroyed by silence.
 *
 * The type names are a live view of the catalog, so renaming a type in Settings corrects the
 * label here and on the card together.
 */
export function EditIdentificationModal({
  open,
  record,
  onClose,
  onSaved,
}: {
  open: boolean;
  record: PersonnelDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [types, setTypes] = useState<IdentificationType[]>([]);
  /*
    A key exists ONLY for a number this form knows — one it revealed, or one the reader typed.
    An absent key is "not supplied", and `build` leaves those out of the request entirely so the
    server leaves them alone. That distinction is what makes a failed reveal harmless.
  */
  const [numbers, setNumbers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    setLoading(true);

    identificationApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setTypes(next);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open]);

  /**
   * Fetches the numbers this record holds — one audited reveal per card.
   *
   * A card whose reveal FAILS is left out of `numbers` rather than seeded blank. That is the
   * whole safety of this form: an absent key is sent as null and the number survives, where a
   * blank would be sent as an empty string and remove it.
   */
  async function seed() {
    const revealed = await Promise.all(
      record.identifications.map(async (value) => {
        try {
          const number = await personnelApi.revealIdentificationNumber(record.id, value.id);
          return [value.identificationTypeId, number] as const;
        } catch {
          /*
            Swallowed deliberately, and this is the one place that is right: a reader without
            personnel.reveal gets a form they can still use for the place and the dates, and a
            banner naming a permission they cannot grant themselves would report a problem they
            cannot act on. The field renders empty and is never sent.
          */
          return null;
        }
      }),
    );

    setNumbers(Object.fromEntries(revealed.filter((entry) => entry !== null)));
  }

  /* Autogenerate on an EDIT form — allowed since 14 Sep 2026. One number per type the
     catalog actually holds, so nothing is invented for a type that does not exist. */
  function generate() {
    setNumbers(
      samplePersonnel({ catalogs: NO_CATALOGS, identificationTypes: types }).identifications,
    );
  }

  function build() {
    return {
      /*
        ONLY the types this form actually knows a number for are sent, and that is the contract
        the server reads:

          absent   — not supplied. The recorded number is LEFT ALONE. This is a card whose
                     reveal failed, which is what a reader without personnel.reveal gets.
          ''       — revealed and then cleared. The number is REMOVED.
          a value  — recorded, replacing whatever was there.

        A blank used to be dropped here, and under the old server contract that removed the
        number because the update rewrote the whole set from the request. It cannot be dropped
        now: dropping it would mean "leave alone", and clearing a field would silently do
        nothing.
      */
      identifications: Object.entries(numbers).map(([identificationTypeId, number]) => ({
        identificationTypeId,
        number: number.trim(),
      })),
    };
  }

  return (
    <PersonnelSectionModal
      open={open}
      record={record}
      title="Edit identification numbers"
      idPrefix="personnel-identification"
      onClose={onClose}
      onSaved={onSaved}
      onOpen={seed}
      onGenerate={generate}
      build={build}
    >
      <Fields types={types} loading={loading} numbers={numbers} setNumbers={setNumbers} />
    </PersonnelSectionModal>
  );
}

function Fields({
  types,
  loading,
  numbers,
  setNumbers,
}: {
  types: IdentificationType[];
  loading: boolean;
  numbers: Record<string, string>;
  setNumbers: (next: Record<string, string>) => void;
}) {
  const { saving, error } = useSectionForm();

  if (loading) {
    return <p className="text-sm text-slate-600">Loading the identification types…</p>;
  }

  /*
    An empty catalog is not an empty form, it is a module with nothing configured — and the
    message says where to go rather than leaving the reader with a modal that has no fields
    and no explanation.
  */
  if (types.length === 0) {
    return (
      <EmptyState message="No identification types are configured. They are maintained in Human Resources → Organization → Settings → Identification." />
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {types.map((type) => (
        <TextField
          key={type.id}
          id={`personnel-identification-${type.id}`}
          label={type.name}
          description={type.code ?? undefined}
          value={numbers[type.id] ?? ''}
          onChange={(e) => setNumbers({ ...numbers, [type.id]: e.target.value })}
          error={error?.fieldError('identifications')}
          disabled={saving}
        />
      ))}
    </div>
  );
}
